/**
 * SUSI — product view + product form.
 *
 * <product-view data-product-root>
 *   Root of one product instance (product page, featured product, quick buy).
 *   Attributes: data-context="main|featured|quickbuy", data-section-id, data-product-id,
 *               data-url (product URL), data-update-url="true|false".
 *   API:  loadVariant(url, { productChange, focusId })  fetches the section for another
 *                                                         variant / product and swaps the regions
 *         form, picker, variantIdInput, context           getters
 *   Regions: every [data-variant-region="key"] inside the root is replaced by the element with
 *            the same key from the fetched section. A product change (combined listing) replaces
 *            the whole root content plus [data-product-region] elements of the section.
 *   Emits (from the variant picker, bubbling): on:variant:change { form, variant, selectedOptions }
 *
 * <product-form>
 *   Wraps the product <form data-product-form>. Adds through theme.cart.add unless the
 *   "after add to cart" setting is 'no-js'.
 *   API:  submit({ opener }) → Promise<boolean>, validate() → boolean, showError(message)
 */
(() => {
  if (customElements.get('product-view')) return;

  const theme = window.theme;

  // Scripts the parser has already executed. Markup that arrives later through DOM insertion
  // (variant changes, quick buy) carries <script src> tags that never run by themselves.
  const executedScripts = new Set(Array.from(document.scripts, (script) => script.src).filter(Boolean));

  class ProductView extends HTMLElement {
    static get observedAttributes() {
      return ['data-context'];
    }

    connectedCallback() {
      if (this.context === 'quickbuy') this.adaptForQuickBuy(this);
      this.runScripts(this);

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.addEventListener('shopify:block:select', this.onBlockSelect);
        this.addEventListener('shopify:block:deselect', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      if (this.request) this.request.abort();
      if (this.onBlockSelect) {
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.removeEventListener('shopify:block:deselect', this.onBlockSelect);
      }
    }

    /** Theme editor: a selected collapsible tab opens so its content can be seen while editing. */
    onBlockSelect(event) {
      const disclosure = event.target.querySelector ? event.target.querySelector('details-disclosure') : null;
      if (!disclosure) return;
      const details = disclosure.querySelector('details');
      if (event.type === 'shopify:block:select') {
        disclosure.dataset.editorOpened = details && details.open ? 'false' : 'true';
        if (typeof disclosure.open === 'function') disclosure.open();
        else if (details) details.open = true;
      } else if (disclosure.dataset.editorOpened === 'true') {
        if (typeof disclosure.close === 'function') disclosure.close();
        else if (details) details.open = false;
      }
    }

    attributeChangedCallback(name, previous, value) {
      if (this.isConnected && value === 'quickbuy' && previous !== value) this.adaptForQuickBuy(this);
    }

    get context() {
      return this.dataset.context || 'main';
    }

    get form() {
      return this.querySelector('form[data-product-form]');
    }

    get picker() {
      return this.querySelector('variant-picker');
    }

    get variantIdInput() {
      const form = this.form;
      return form ? form.querySelector('input[name="id"]') : null;
    }

    /**
     * Strips everything that should not travel into a quick buy drawer.
     * Runs on the root when its context becomes 'quickbuy' and on every region swapped in later.
     * @param {Element} scope
     */
    adaptForQuickBuy(scope) {
      scope.querySelectorAll('[data-quickbuy="hide"]').forEach((element) => element.remove());
      scope.querySelectorAll('[data-quickbuy="only"]').forEach((element) => {
        element.hidden = false;
      });
      scope.querySelectorAll('.product__sticky').forEach((element) => element.classList.remove('product__sticky'));

      scope.querySelectorAll('[data-product-title]').forEach((title) => {
        if (title.querySelector('a')) return;
        const link = document.createElement('a');
        link.href = this.dataset.url;
        while (title.firstChild) link.appendChild(title.firstChild);
        title.appendChild(link);
      });

      scope.querySelectorAll('media-gallery').forEach((gallery) => {
        gallery.dataset.layout = 'carousel-under';
        gallery.dataset.context = 'quickbuy';
        gallery.removeAttribute('data-zoom');
        gallery.querySelectorAll('[data-gallery-zoom]').forEach((link) => {
          link.removeAttribute('data-gallery-zoom');
          link.href = this.dataset.url;
        });
      });
    }

    /**
     * Fetches the section rendered for another variant (or sibling product) and applies it.
     * @param {string|URL} url - Product URL including section_id and variant / option_values.
     * @param {object} [options] - { productChange: boolean, focusId: string }
     * @returns {Promise<void>}
     */
    async loadVariant(url, options = {}) {
      if (this.request) this.request.abort();
      const request = new AbortController();
      this.request = request;
      this.classList.add('is-updating');

      try {
        const doc = await theme.fetchDocument(url.toString(), { cache: true, signal: request.signal });
        if (request.signal.aborted) return;
        this.applyDocument(doc, options);
      } catch (error) {
        if (error.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('[SUSI] variant request failed', error);
        }
      } finally {
        if (this.request === request) {
          this.request = null;
          this.classList.remove('is-updating');
        }
      }
    }

    /**
     * @param {Document} doc - Parsed section HTML.
     * @param {object} [options] - { productChange: boolean, focusId: string }
     */
    applyDocument(doc, options = {}) {
      const fresh = doc.querySelector('[data-product-root]');
      if (!fresh) return;

      if (options.productChange) {
        this.replaceProduct(fresh, doc);
      } else {
        this.swapRegions(fresh);
      }

      this.runScripts(this);

      const picker = this.picker;
      const variant = picker ? picker.variant : null;
      const selectedOptions = picker ? picker.selectedOptions : [];
      this.syncVariantId(variant);

      if (options.focusId) {
        // Microtask: lets a host that rewrites ids of inserted markup (quick buy) finish first
        queueMicrotask(() => {
          const target = document.getElementById(options.focusId);
          if (target && this.contains(target)) target.focus({ preventScroll: true });
        });
      }

      if (picker) picker.refresh();
      if (options.productChange || (picker && !selectedOptions.includes(null))) this.updateUrl(variant);
      if (picker) theme.emit('on:variant:change', { form: this.form, variant, selectedOptions }, picker);
    }

    swapRegions(fresh) {
      this.querySelectorAll('[data-variant-region]').forEach((current) => {
        if (!current.isConnected) return;
        const key = current.dataset.variantRegion;
        const next = fresh.querySelector(`[data-variant-region="${CSS.escape(key)}"]`);
        if (!next) return;

        if (this.context === 'quickbuy') this.adaptForQuickBuy(next);

        // Keep Shop Pay instalments from collapsing while their component boots again
        const terms = current.querySelector('shopify-payment-terms');
        const nextTerms = next.querySelector('shopify-payment-terms');
        if (terms && nextTerms && terms.clientHeight) {
          nextTerms.style.minHeight = `${terms.clientHeight}px`;
          setTimeout(() => {
            nextTerms.style.minHeight = '';
          }, 3000);
        }

        current.replaceWith(next);
      });
    }

    replaceProduct(fresh, doc) {
      theme.pauseAllMedia(this);
      ['productId', 'url', 'sectionId'].forEach((key) => {
        if (fresh.dataset[key]) this.dataset[key] = fresh.dataset[key];
      });
      if (this.context === 'quickbuy') this.adaptForQuickBuy(fresh);
      this.replaceChildren(...Array.from(fresh.childNodes));

      const section = this.closest('.shopify-section');
      if (section && this.context === 'main') {
        section.querySelectorAll('[data-product-region]').forEach((current) => {
          const next = doc.querySelector(`[data-product-region="${CSS.escape(current.dataset.productRegion)}"]`);
          if (next) current.replaceWith(next);
        });
      }
    }

    /** Loads the component scripts referenced inside a scope that have not run on this page yet. */
    runScripts(scope) {
      scope.querySelectorAll('script[src]').forEach((script) => {
        if (executedScripts.has(script.src)) return;
        executedScripts.add(script.src);
        theme.loadScript(script.src);
      });
    }

    syncVariantId(variant) {
      const input = this.variantIdInput;
      if (!input) return;
      const value = variant ? String(variant.id) : '';
      if (input.value === value) return;
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateUrl(variant) {
      if (this.context !== 'main' || this.dataset.updateUrl !== 'true') return;
      const url = new URL(this.dataset.url, window.location.origin);
      if (variant) url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.root = this.closest('[data-product-root]') || this.parentElement;
      this.onSubmit = this.onSubmit.bind(this);
      this.onInput = () => this.showError('');

      if (theme.settings && theme.settings.afterAddToCart !== 'no-js') {
        this.form.addEventListener('submit', this.onSubmit);
      }
      this.form.addEventListener('change', this.onInput);
      this.prepareDynamicCheckout();
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this.onSubmit);
      this.form.removeEventListener('change', this.onInput);
      if (this.idInput && this.onIdChange) this.idInput.removeEventListener('change', this.onIdChange);
    }

    get idInput() {
      return this.form.querySelector('input[name="id"]');
    }

    get submitButton() {
      return this.form.querySelector('[data-add-to-cart]');
    }

    get errorBox() {
      return this.querySelector('[data-form-error]');
    }

    /** The accelerated checkout button only makes sense once a variant is chosen. */
    prepareDynamicCheckout() {
      const holder = this.querySelector('[data-dynamic-checkout]');
      const template = holder ? holder.querySelector('template') : null;
      if (!template) return;

      const mount = () => {
        if (!this.idInput || !this.idInput.value) return false;
        holder.appendChild(template.content.cloneNode(true));
        template.remove();
        if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        return true;
      };

      if (mount()) return;
      this.onIdChange = () => {
        if (mount()) this.idInput.removeEventListener('change', this.onIdChange);
      };
      this.idInput.addEventListener('change', this.onIdChange);
    }

    onSubmit(event) {
      event.preventDefault();
      this.submit({ opener: event.submitter || this.submitButton });
    }

    /**
     * Checks the option selection and every field attached to the form.
     * @returns {boolean}
     */
    validate() {
      const picker = this.root.querySelector('variant-picker');
      if (picker && typeof picker.validate === 'function' && !picker.validate()) return false;

      if (!this.form.reportValidity()) return false;
      return Boolean(this.idInput && this.idInput.value);
    }

    /**
     * Validates and adds to the cart.
     * @param {object} [options] - { opener: Element } element that regains focus when a drawer closes.
     * @returns {Promise<boolean>} Whether the item was added.
     */
    async submit(options = {}) {
      const button = this.submitButton;
      if (this.busy || (button && button.disabled)) return false;
      this.showError('');
      if (!this.validate()) return false;

      this.busy = true;
      if (button) {
        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');
      }

      try {
        await theme.cart.add(new FormData(this.form), { opener: options.opener || button });
        return true;
      } catch (error) {
        this.showError(ProductForm.describe(error));
        return false;
      } finally {
        this.busy = false;
        const current = this.submitButton;
        [button, current].forEach((element) => {
          if (!element) return;
          element.classList.remove('is-loading');
          element.removeAttribute('aria-disabled');
        });
      }
    }

    static describe(error) {
      const data = error && error.data;
      if (data && data.errors && typeof data.errors === 'object') {
        return Object.values(data.errors)
          .map((messages) => (Array.isArray(messages) ? messages.join(', ') : String(messages)))
          .join(' ');
      }
      if (data && typeof data.description === 'string') return data.description;
      return (error && error.message) || '';
    }

    showError(message) {
      const box = this.errorBox;
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }
  }

  customElements.define('product-view', ProductView);
  customElements.define('product-form', ProductForm);
})();
